import React, { useState, useCallback } from 'react';
import { Heart, Lightbulb, Activity, Clock, Calendar, Bell, Moon, Sun, Coffee, AlertTriangle, Zap, ThermometerSun, Download, Mail, TrendingDown, Gauge, Scale, X, Plus, Save } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter } from 'recharts';

interface DailyReport {
  date: Date;
  activities: {
    type: string;
    duration: number;
    engagement: number;
    location: string;
    timeOfDay: string;
  }[];
  wellness: {
    mood: 'good' | 'neutral' | 'concerned';
    sleep: {
      hours: number;
      quality: number;
      disruptions: number;
    };
    nutrition: {
      meals: number;
      hydration: number;
    };
    socialInteractions: number;
  };
  vitals: {
    heartRate: number;
    bloodPressure: string;
    temperature: number;
  };
  gaitMetrics: {
    walkingSpeed: number;
    strideVariability: number;
    distanceCovered: number;
  };
  cognitiveStatus: {
    spatialConfusion: number;
    repetitivePatterns: number;
    successfulNavigations: number;
  };
  environmentalFactors: {
    temperature: number;
    humidity: number;
    noise: number;
    light: number;
  };
  alerts: {
    type: 'behavioral' | 'health' | 'safety' | 'environmental';
    severity: 'low' | 'medium' | 'high';
    message: string;
    timestamp: Date;
  }[];
}

interface PredictiveMetrics {
  fallRiskScore: number;
  fallRiskTrend: 'increasing' | 'decreasing' | 'stable';
  cognitiveDeclineProbability: number;
  nextMonthPredictions: {
    expectedGaitSpeed: number;
    expectedSocialInteractions: number;
    recommendedInterventions: string[];
  };
  riskFactors: {
    factor: string;
    severity: 'low' | 'medium' | 'high';
    trend: string;
  }[];
}

interface AlertConfig {
  type: 'behavioral' | 'health' | 'safety' | 'environmental';
  enabled: boolean;
  threshold: number;
  notifyVia: ('email' | 'sms' | 'app')[];
}

interface CareNote {
  id: string;
  title: string;
  content: string;
  author: string;
  timestamp: Date;
  category: 'medication' | 'therapy' | 'general' | 'behavior';
}

// Enhanced mock data generation with time ranges
const generateMockData = (timeRange: 'day' | 'week' | 'month'): { dailyReport: DailyReport; predictiveMetrics: PredictiveMetrics } => {
  const now = new Date();
  const multiplier = timeRange === 'day' ? 1 : timeRange === 'week' ? 7 : 30;
  
  const predictiveMetrics: PredictiveMetrics = {
    fallRiskScore: 35 + Math.random() * 20,
    fallRiskTrend: ['increasing', 'decreasing', 'stable'][Math.floor(Math.random() * 3)] as 'increasing' | 'decreasing' | 'stable',
    cognitiveDeclineProbability: 15 + Math.random() * 10,
    nextMonthPredictions: {
      expectedGaitSpeed: 0.8 + Math.random() * 0.2,
      expectedSocialInteractions: 8 + Math.random() * 4,
      recommendedInterventions: [
        'Increase physical therapy sessions',
        'Implement cognitive exercises',
        'Enhance social engagement activities',
        'Adjust medication schedule'
      ]
    },
    riskFactors: [
      {
        factor: 'Gait Asymmetry',
        severity: 'medium',
        trend: 'Improving with therapy'
      },
      {
        factor: 'Balance Control',
        severity: 'high',
        trend: 'Needs attention'
      },
      {
        factor: 'Stride Length Variability',
        severity: 'low',
        trend: 'Stable'
      }
    ]
  };

  return {
    dailyReport: {
      date: now,
      activities: [
        {
          type: 'Exercise',
          duration: 30 * multiplier,
          engagement: 0.8,
          location: 'Activity Room',
          timeOfDay: 'Morning'
        },
        {
          type: 'Social',
          duration: 45 * multiplier,
          engagement: 0.9,
          location: 'Common Area',
          timeOfDay: 'Afternoon'
        },
        {
          type: 'Cognitive',
          duration: 25 * multiplier,
          engagement: 0.75,
          location: 'Library',
          timeOfDay: 'Morning'
        }
      ],
      wellness: {
        mood: 'good',
        sleep: {
          hours: 7.5,
          quality: 0.85,
          disruptions: 2 * multiplier
        },
        nutrition: {
          meals: 3 * multiplier,
          hydration: 0.8
        },
        socialInteractions: 12 * multiplier
      },
      vitals: {
        heartRate: 72,
        bloodPressure: '120/80',
        temperature: 98.6
      },
      gaitMetrics: {
        walkingSpeed: 0.9,
        strideVariability: 0.04,
        distanceCovered: 1250 * multiplier
      },
      cognitiveStatus: {
        spatialConfusion: Math.max(15 * (multiplier * 0.8), 100),
        repetitivePatterns: Math.min(3 * multiplier, 20),
        successfulNavigations: 45
      },
      environmentalFactors: {
        temperature: 72,
        humidity: 45,
        noise: 35,
        light: 500
      },
      alerts: [
        {
          type: 'behavioral',
          severity: 'low',
          message: 'Slight increase in wandering behavior during evening hours',
          timestamp: new Date(now.getTime() - 3600000)
        }
      ]
    },
    predictiveMetrics
  };
};

const FamilyCaregiverPortal: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month'>('day');
  const [selectedResident, setSelectedResident] = useState<string>('John Doe');
  const [showReportModal, setShowReportModal] = useState(false);
  const { dailyReport, predictiveMetrics } = generateMockData(timeRange);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [alertConfigs, setAlertConfigs] = useState<AlertConfig[]>([
    {
      type: 'behavioral',
      enabled: true,
      threshold: 70,
      notifyVia: ['email', 'app']
    },
    {
      type: 'health',
      enabled: true,
      threshold: 80,
      notifyVia: ['email', 'sms', 'app']
    },
    {
      type: 'safety',
      enabled: true,
      threshold: 90,
      notifyVia: ['email', 'sms', 'app']
    },
    {
      type: 'environmental',
      enabled: false,
      threshold: 60,
      notifyVia: ['app']
    }
  ]);
  const [careNotes, setCareNotes] = useState<CareNote[]>([
    {
      id: '1',
      title: 'Morning Medication Update',
      content: 'All medications taken as scheduled. Resident was alert and cooperative.',
      author: 'Nurse Sarah',
      timestamp: new Date(Date.now() - 3600000 * 2),
      category: 'medication'
    },
    {
      id: '2',
      title: 'Physical Therapy Session',
      content: 'Completed 30-minute walking exercise. Showing improvement in balance.',
      author: 'PT John',
      timestamp: new Date(Date.now() - 3600000),
      category: 'therapy'
    }
  ]);
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    category: 'general' as const
  });

  const handleExportReport = () => {
    // Mock export functionality
    console.log('Exporting report...');
    setTimeout(() => {
      alert('Report exported successfully!');
    }, 1000);
  };

  const handleEmailReport = () => {
    // Mock email functionality
    console.log('Sending report via email...');
    setTimeout(() => {
      alert('Report sent successfully to registered family members!');
    }, 1000);
  };

  const handleSaveAlertConfigs = (configs: AlertConfig[]) => {
    setAlertConfigs(configs);
    setShowAlertModal(false);
    alert('Alert preferences saved successfully!');
  };

  const handleAddNote = () => {
    if (!newNote.title || !newNote.content) {
      alert('Please fill in all required fields');
      return;
    }

    const note: CareNote = {
      id: Date.now().toString(),
      ...newNote,
      author: 'Dr. Smith', // In a real app, this would come from the logged-in user
      timestamp: new Date(),
    };

    setCareNotes(prev => [note, ...prev]);
    setNewNote({ title: '', content: '', category: 'general' });
    setShowNoteModal(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Heart className="text-purple-600" />
            Family & Caregiver Portal
          </h2>
          <p className="text-gray-600 mt-1">
            Comprehensive monitoring and care insights for {selectedResident}
          </p>
        </div>
        <div className="flex gap-4">
          <select
            className="border rounded-lg px-3 py-2"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as 'day' | 'week' | 'month')}
          >
            <option value="day">Today</option>
            <option value="week">Past Week</option>
            <option value="month">Past Month</option>
          </select>
          <button 
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
          >
            <Mail size={18} />
            Deliver Report
          </button>
          <button 
            onClick={() => setShowAlertModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Bell size={18} />
            Configure Alerts
          </button>
        </div>
      </div>

      {/* Quick Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="text-purple-600" />
            <h3 className="font-semibold">Cognitive Status</h3>
          </div>
          <div className="text-2xl font-bold">
            {dailyReport.cognitiveStatus.successfulNavigations}%
          </div>
          <p className="text-sm text-gray-600">Navigation Success Rate</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="text-green-600" />
            <h3 className="font-semibold">Physical Activity</h3>
          </div>
          <div className="text-2xl font-bold">
            {dailyReport.gaitMetrics.distanceCovered}m
          </div>
          <p className="text-sm text-gray-600">Distance Covered Today</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Moon className="text-blue-600" />
            <h3 className="font-semibold">Sleep Quality</h3>
          </div>
          <div className="text-2xl font-bold">
            {dailyReport.wellness.sleep.hours}hrs
          </div>
          <p className="text-sm text-gray-600">Last Night's Sleep</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Coffee className="text-orange-600" />
            <h3 className="font-semibold">Social Engagement</h3>
          </div>
          <div className="text-2xl font-bold">
            {dailyReport.wellness.socialInteractions}
          </div>
          <p className="text-sm text-gray-600">Social Interactions Today</p>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Timeline */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Daily Activity Timeline</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyReport.activities}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timeOfDay" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="engagement" 
                  stroke="#8b5cf6" 
                  fill="#8b5cf6" 
                  fillOpacity={0.3}
                  name="Engagement Level"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Wellness Metrics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Wellness Overview</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Sleep Quality</div>
              <div className="text-xl font-semibold text-purple-600">
                {(dailyReport.wellness.sleep.quality * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-gray-500">
                {dailyReport.wellness.sleep.disruptions} disruptions
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Hydration</div>
              <div className="text-xl font-semibold text-blue-600">
                {(dailyReport.wellness.nutrition.hydration * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-gray-500">of daily goal</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Nutrition</div>
              <div className="text-xl font-semibold text-green-600">
                {dailyReport.wellness.nutrition.meals}/3
              </div>
              <div className="text-xs text-gray-500">meals taken</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Social Activity</div>
              <div className="text-xl font-semibold text-orange-600">
                {dailyReport.wellness.socialInteractions}
              </div>
              <div className="text-xs text-gray-500">interactions today</div>
            </div>
          </div>
        </div>

        {/* Gait & Movement Analysis */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Movement Patterns</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-600">Walking Speed</div>
                <div className="text-xl font-semibold">
                  {dailyReport.gaitMetrics.walkingSpeed} m/s
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Stride Variability</div>
                <div className="text-xl font-semibold">
                  {(dailyReport.gaitMetrics.strideVariability * 100).toFixed(1)}%
                </div>
              </div>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[dailyReport.gaitMetrics]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timeOfDay" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="walkingSpeed" 
                    stroke="#8b5cf6" 
                    name="Walking Speed"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Environmental Conditions */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Environmental Conditions</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <ThermometerSun className="text-orange-500" />
                <div>
                  <div className="text-sm text-gray-600">Temperature</div>
                  <div className="text-xl font-semibold">
                    {dailyReport.environmentalFactors.temperature}°F
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Sun className="text-yellow-500" />
                <div>
                  <div className="text-sm text-gray-600">Light Level</div>
                  <div className="text-xl font-semibold">
                    {dailyReport.environmentalFactors.light} lux
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Zap className="text-blue-500" />
                <div>
                  <div className="text-sm text-gray-600">Humidity</div>
                  <div className="text-xl font-semibold">
                    {dailyReport.environmentalFactors.humidity}%
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-purple-500" />
                <div>
                  <div className="text-sm text-gray-600">Noise Level</div>
                  <div className="text-xl font-semibold">
                    {dailyReport.environmentalFactors.noise} dB
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Alerts & Notifications */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Alerts & Updates</h3>
        <div className="space-y-4">
          {dailyReport.alerts.map((alert, index) => (
            <div 
              key={index}
              className={`flex items-start gap-4 p-4 rounded-lg ${
                alert.severity === 'high' ? 'bg-red-50' :
                alert.severity === 'medium' ? 'bg-yellow-50' :
                'bg-blue-50'
              }`}
            >
              <AlertTriangle className={
                alert.severity === 'high' ? 'text-red-500' :
                alert.severity === 'medium' ? 'text-yellow-500' :
                'text-blue-500'
              } />
              <div>
                <div className="font-medium">{alert.message}</div>
                <div className="text-sm text-gray-600">
                  {new Date(alert.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Updated Care Team Notes & Communication */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Care Team Communication</h3>
          <button 
            onClick={() => setShowNoteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
          >
            <Plus size={18} />
            Add Note
          </button>
        </div>
        <div className="space-y-4">
          {careNotes.map(note => (
            <div 
              key={note.id} 
              className={`border-l-4 pl-4 py-2 ${
                note.category === 'medication' ? 'border-purple-500' :
                note.category === 'therapy' ? 'border-green-500' :
                note.category === 'behavior' ? 'border-orange-500' :
                'border-gray-500'
              }`}
            >
              <div className="font-medium">{note.title}</div>
              <div className="text-sm text-gray-600">{note.content}</div>
              <div className="text-xs text-gray-500 mt-1">
                {new Date(note.timestamp).toLocaleString()} - {note.author}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fall Risk & Predictive Analytics Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Predictive Health Insights</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="text-orange-600" />
              <h4 className="font-semibold">Fall Risk Assessment</h4>
            </div>
            <div className="text-3xl font-bold text-orange-600 mb-2">
              {predictiveMetrics.fallRiskScore.toFixed(1)}%
            </div>
            <div className="text-sm text-orange-800">
              Trend: {predictiveMetrics.fallRiskTrend.charAt(0).toUpperCase() + predictiveMetrics.fallRiskTrend.slice(1)}
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Gauge className="text-blue-600" />
              <h4 className="font-semibold">Gait Analysis</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-800">Walking Speed</span>
                <span className="font-semibold">{dailyReport.gaitMetrics.walkingSpeed} m/s</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-800">Stride Variability</span>
                <span className="font-semibold">{(dailyReport.gaitMetrics.strideVariability * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-800">Distance Covered</span>
                <span className="font-semibold">{dailyReport.gaitMetrics.distanceCovered}m</span>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Scale className="text-purple-600" />
              <h4 className="font-semibold">Cognitive Decline Risk</h4>
            </div>
            <div className="text-3xl font-bold text-purple-600 mb-2">
              {predictiveMetrics.cognitiveDeclineProbability.toFixed(1)}%
            </div>
            <div className="text-sm text-purple-800">
              Based on spatial navigation patterns
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="font-semibold mb-3">Risk Factors & Recommendations</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              {predictiveMetrics.riskFactors.map((factor, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div>
                    <div className="font-medium">{factor.factor}</div>
                    <div className="text-sm text-gray-600">{factor.trend}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    factor.severity === 'high' ? 'bg-red-100 text-red-800' :
                    factor.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {factor.severity.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-semibold mb-2">Recommended Interventions</h5>
              <ul className="space-y-2">
                {predictiveMetrics.nextMonthPredictions.recommendedInterventions.map((intervention, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-2 h-2 mt-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm">{intervention}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Report Delivery Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Deliver Report</h3>
            <div className="space-y-4">
              <button
                onClick={handleExportReport}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
              >
                <Download size={18} />
                Export as PDF
              </button>
              <button
                onClick={handleEmailReport}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Mail size={18} />
                Email to Family Members
              </button>
              <button
                onClick={() => setShowReportModal(false)}
                className="w-full px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Configuration Modal */}
      {showAlertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg w-full max-w-2xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold">Configure Alert Preferences</h3>
              <button 
                onClick={() => setShowAlertModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {alertConfigs.map((config, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="text-purple-600" />
                        <h4 className="font-semibold capitalize">{config.type} Alerts</h4>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={config.enabled}
                          onChange={() => {
                            const newConfigs = [...alertConfigs];
                            newConfigs[index].enabled = !config.enabled;
                            setAlertConfigs(newConfigs);
                          }}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Alert Threshold</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={config.threshold}
                          onChange={(e) => {
                            const newConfigs = [...alertConfigs];
                            newConfigs[index].threshold = parseInt(e.target.value);
                            setAlertConfigs(newConfigs);
                          }}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="text-sm text-gray-600 mt-1">{config.threshold}% threshold</div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 mb-2 block">Notification Methods</label>
                        <div className="flex gap-4">
                          {(['email', 'sms', 'app'] as const).map(method => (
                            <label key={method} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={config.notifyVia.includes(method)}
                                onChange={() => {
                                  const newConfigs = [...alertConfigs];
                                  if (config.notifyVia.includes(method)) {
                                    newConfigs[index].notifyVia = config.notifyVia.filter(m => m !== method);
                                  } else {
                                    newConfigs[index].notifyVia = [...config.notifyVia, method];
                                  }
                                  setAlertConfigs(newConfigs);
                                }}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                              <span className="capitalize">{method}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t p-6 bg-white">
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowAlertModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveAlertConfigs(alertConfigs)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Save size={18} />
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Add Care Note</h3>
              <button 
                onClick={() => setShowNoteModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note Title
                </label>
                <input
                  type="text"
                  value={newNote.title}
                  onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter note title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={newNote.category}
                  onChange={(e) => setNewNote(prev => ({ ...prev, category: e.target.value as any }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="general">General</option>
                  <option value="medication">Medication</option>
                  <option value="therapy">Therapy</option>
                  <option value="behavior">Behavior</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note Content
                </label>
                <textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-32"
                  placeholder="Enter note details"
                />
              </div>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowNoteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Save size={18} />
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyCaregiverPortal; 
